import * as React from 'react';
import Link from 'next/link';
import styled from 'styled-components';
import { Page } from '@packages/common';
import { WishlistHelperContext, AppStaticStateContext } from '@packages/shared';
import { Translate } from '@packages/i18n';
import { IconNames, IconWithLabel, Button, media, themeGet, Flex, Box } from '@packages/ui';
import { getPublicRuntimeConfig } from '@helpers/getPublicRuntimeConfig';

const ROUTES = getPublicRuntimeConfig('ROUTES');

const Wrapper = styled(Flex)`
  align-items: center;
  flex: 0 1 240px;
  justify-content: flex-end;
  overflow: hidden;

  .searchButton {
    padding: 0;
    & > :first-child {
      height: 100%;
    }

    ${media.from.lg`
      display: none;
    `}
  }

  .navigationIcon {
    line-height: ${themeGet.fontSize(7)};
    height: 42px;
    flex: 0 1 72px;
    ${media.to.sm`
      overflow: hidden;

      /* we only want the text to have width 100% to overflow correctly and show
      ellipsis */
      & span:nth-child(2) {
        width: 100%;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    `}
  }
`;

export const WishlistItem: React.FC = React.memo(() => {
  const { wishlistHelper } = React.useContext(WishlistHelperContext);
  const wishlistedItemsIdsInitial = wishlistHelper.getWishlistedItemsIds().length;
  const [icon, setIcon] = React.useState<IconNames>(wishlistedItemsIdsInitial > 0 ? 'heartFilled' : 'heart');
  const [badgeCount, setBadgeCount] = React.useState(wishlistHelper.getWishlistedItemsIds().length);

  React.useEffect(() => {
    const sub = wishlistHelper.subscribe((next) => {
      if (next.length > 0) {
        setIcon('heartFilled');
        setBadgeCount(next.length);
      } else {
        setIcon('heart');
        setBadgeCount(0);
      }
    });
    return () => {
      sub.unsubscribe();
    };
  }, [wishlistHelper]);

  return (
    <IconWithLabel icon={icon} badgeCount={badgeCount}>
      <Translate translation="wishlist" />
    </IconWithLabel>
  );
});

WishlistItem.displayName = 'WishlistItem';

export const IconNavigation: React.FC<{ toggleSearch: () => void }> = React.memo(({ toggleSearch }) => {
  const fakeInputRef = React.useRef(null);
  const { browser } = React.useContext(AppStaticStateContext);

  // Mobile Safari string detects both mobile and tablet
  const isMobileSafari = browser === 'Mobile Safari';

  const onClick = React.useCallback(() => {
    if (fakeInputRef.current) {
      fakeInputRef.current.focus();
    }
    toggleSearch();
  }, [toggleSearch]);

  return (
    <Wrapper>
      {/* 
          Safari on mobile/tablet requires a hidden input, which is activated at the moment the search button is clicked.
          This toggles the keyboard. The focus with open keyboard is transferred to real input at later point of time elsewhere.
          Safari only allows keyboard toggling from function, to which click event was passed synchroniously.
         */}
      {isMobileSafari && (
        <input
          ref={fakeInputRef}
          type="text"
          style={{ position: 'absolute', zIndex: -100000, opacity: '0', width: '0px', height: '0px' }}
        ></input>
      )}
      <Button
        variant="clean"
        onClick={onClick}
        className="navigationIcon searchButton"
        data-testid="mobile-search-toggle"
      >
        <IconWithLabel icon="search">
          <Translate translation="search"></Translate>
        </IconWithLabel>
      </Button>
      <Box className="navigationIcon">
        <Link href={ROUTES.wishlist} prefetch={false}>
          <a data-testid="wishlist-page-link">
            <WishlistItem />
          </a>
        </Link>
      </Box>
      <Box className="navigationIcon">
        <Link href={Page.localDealers} prefetch={false}>
          <a data-testid="local-dealers-page-link">
            <IconWithLabel icon="marker">
              <Translate translation="dealer"></Translate>
            </IconWithLabel>
          </a>
        </Link>
      </Box>
    </Wrapper>
  );
});

IconNavigation.displayName = 'IconNavigation';
